/**
 * An IELTS sitting (D-105): the state sent by the server (Listening parts, Reading sections,
 * Writing tasks, Speaking parts, placement), the results in bands and the review after the end.
 * Shapes of apps/academy/ielts_engine.py (`state`, `results`) and api.py (`review`).
 */

import { type ExamPicture, parsePicture } from "../pictures";
import { type Item, type ItemResponse, parseItem, parseResponse } from "./items";
import { type LocalizedText, localizedText } from "./course";
import { isObject, num, numOrNull, objects, str, strings } from "./read";

/** The four skills, with the TCF codes the server uses for both exams. */
export type IeltsSkill = "co" | "ce" | "ee" | "eo";
export const IELTS_SKILLS: readonly IeltsSkill[] = ["co", "ce", "ee", "eo"];

export function isIeltsSkill(value: unknown): value is IeltsSkill {
  return typeof value === "string" && (IELTS_SKILLS as readonly string[]).includes(value);
}

export interface PassageText {
  title: string;
  text: string;
}

export interface Passage {
  id: string;
  number: number;
  title: string;
  context: string;
  texts: PassageText[];
  instructions: string;
  /** IELTS (D-136): the official rubric of each group of questions (« Questions 1–7: Complete
   * the form below. Write NO MORE THAN TWO WORDS AND/OR A NUMBER for each answer. »), shown
   * above the first question of its group, as on the test. */
  instructionGroups: InstructionGroup[];
  /** Listening plan/map labelling (D-136): the plan described in words, shown with the questions. */
  mapDescription: string;
  scriptForSpeech: string;
  hasAudio: boolean;
  /** TEF (D-109): the type of the document (the key of its section). */
  docType: string;
  /** The picture of the part (IELTS plan or map to label), or null. */
  image: ExamPicture | null;
  /** TEF CE (D-163): the figures of the document (the four graphs of section 3), or null. */
  table: PassageTable | null;
}

/** A document's figures: the first column holds the x values (years), each other column one
 * series (« Graphique 1 » …). Cells are strings as written in the seed. */
export interface PassageTable {
  caption: string;
  columns: string[];
  rows: string[][];
}

export interface ChartSeries {
  label: string;
  points: { x: string; y: number | null }[];
  min: number;
  max: number;
}

export function parseTable(value: unknown): PassageTable | null {
  if (!isObject(value) || !Array.isArray(value.columns) || !Array.isArray(value.rows)) return null;
  const columns = value.columns.map((c) => str(c));
  const rows = value.rows.filter((r): r is unknown[] => Array.isArray(r)).map((r) => r.map((c) => (typeof c === "number" ? String(c) : str(c))));
  if (columns.length < 2 || !rows.length) return null;
  return { caption: str(value.caption), columns, rows };
}

/** D-163: the figures of the reading documents of a TEF review (api.py `reading`), by passage. */
export function reviewTables(value: unknown): Record<string, PassageTable> {
  const rows = isObject(value) && Array.isArray(value.reading) ? value.reading : [];
  const out: Record<string, PassageTable> = {};
  for (const row of rows) {
    if (!isObject(row)) continue;
    const table = parseTable(row.table);
    if (table && str(row.id)) out[str(row.id)] = table;
  }
  return out;
}

/** The passage (document) of each question of a review, in the order of the review. */
export function reviewPassages(value: unknown): string[] {
  const rows = isObject(value) && Array.isArray(value.questions) ? value.questions : [];
  return rows.map((row) => (isObject(row) ? str(row.passage) : ""));
}

/** One series per column after the first (D-163: one small chart per graph). The scale of all
 * the series is the same, so that the graphs can be compared as on the test. */
export function tableSeries(table: PassageTable | null): ChartSeries[] {
  if (!table) return [];
  const values = table.rows.flatMap((row) => row.slice(1).map((cell) => Number(String(cell).replace(",", "."))).filter((n) => Number.isFinite(n)));
  const min = Math.min(0, ...values);
  const max = values.length ? Math.max(...values) : 0;
  return table.columns.slice(1).map((label, index) => ({
    label,
    min,
    max,
    points: table.rows.map((row) => {
      const n = Number(String(row[index + 1] ?? "").replace(",", "."));
      return { x: row[0] ?? "", y: row[index + 1] !== undefined && row[index + 1] !== "" && Number.isFinite(n) ? n : null };
    }),
  }));
}

export interface InstructionGroup {
  first: number;
  last: number;
  text: string;
}

/** « Questions 11–15: Label the plan… » → {first: 11, last: 15, text} (a list of rubrics; a
 * single rubric written as a string stays in `instructions`). */
export function instructionGroups(value: unknown): InstructionGroup[] {
  const list: unknown[] = Array.isArray(value) ? value : [];
  return list
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((text) => {
      const match = /^\s*Questions?\s+(\d+)(?:\s*[–—-]\s*(\d+)|\s+and\s+(\d+))?\s*:/i.exec(text);
      const first = match ? Number(match[1]) : 0;
      const last = match ? Number(match[2] ?? match[3] ?? match[1]) : 0;
      return { first, last, text: text.trim() };
    });
}

/** The rubric to show above a question: its group's, on the first question of the group only.
 * Rubrics without question numbers are shown above the first question of the part. */
export function instructionFor(groups: readonly InstructionGroup[], number: number | null, numbers: readonly number[] = [], isFirst = false): string[] {
  const covered = numbers.length ? numbers : number === null ? [] : [number];
  return groups.filter((g) => (g.first === 0 ? isFirst : covered.includes(g.first))).map((g) => g.text);
}

/**
 * A plan described in words (« Site plan of … Labelled positions: A – the building …; B – … »)
 * → its introduction and one line per labelled position, for a readable plan on every screen.
 */
export function planLines(description: string): { intro: string; positions: { letter: string; text: string }[] } {
  const [head, list] = description.split(/labell?ed positions\s*:\s*/i);
  if (!list) return { intro: description.trim(), positions: [] };
  const positions = list
    .split(/;\s*(?=[A-Z]\s*[–-]\s)/)
    .map((part) => /^([A-Z])\s*[–-]\s*(.+?)\.?$/.exec(part.trim()))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => ({ letter: m[1] ?? "", text: m[2] ?? "" }));
  return { intro: (head ?? "").trim(), positions };
}

export type SittingQuestion = Item & { response: ItemResponse };

export interface QuestionGroup {
  passage: Passage | null;
  questions: SittingQuestion[];
}

export interface IeltsTask {
  id: string;
  number: number;
  prompt: string;
  bullets: string[];
  minWords: number | null;
  suggestedMinutes: number | null;
  prepSeconds: number;
  seconds: number;
  label: LocalizedText;
  letterType: string;
  text: string;
  status: string;
  hasAudio: boolean;
  cueCard: { topic: string; bullets: string[] } | null;
  prepUntil: string | null;
  topics: string[];
}

export interface IeltsState {
  status: string;
  exam: string;
  kind: string;
  mode: string;
  section: IeltsSkill | "pl" | "";
  remaining: number;
  skills: string[];
  answered: number;
  total: number;
  part: number;
  parts: number;
  /** IELTS on computer (D-163): every Listening answer can be changed until the section ends
   * (`sections` then holds the 4 parts); `part` is the recording being played. */
  navigable: boolean;
  /** Minutes of review after the last recording (IELTS on computer: 2). */
  reviewMinutes: number;
  current: QuestionGroup | null;
  sections: QuestionGroup[];
  tasks: IeltsTask[];
  /** Placement: the exercise being asked and the level being checked. */
  question: Item | null;
  level: string;
  results: IeltsResults;
}

export function parsePassage(value: unknown): Passage | null {
  if (!isObject(value)) return null;
  const data = isObject(value.data) ? value.data : {};
  const texts = objects(value.texts)
    .map((t) => ({ title: str(t.title), text: str(t.text) }))
    .filter((t) => t.text);
  if (!texts.length && str(value.document)) texts.push({ title: str(value.title), text: str(value.document) });
  return {
    id: str(value.id),
    number: num(value.number),
    title: str(value.title),
    context: str(value.context),
    texts,
    instructions: str(data.instructions),
    instructionGroups: instructionGroups(data.instructions),
    mapDescription: str(data.map_description),
    scriptForSpeech: str(value.script_for_speech),
    hasAudio: value.has_audio === true,
    docType: str(value.type),
    image: parsePicture(value.image),
    table: parseTable(data.table),
  };
}

/** A Listening part or a Reading section: its passage and its questions with the answers given. */
export function parseQuestionGroup(value: unknown): QuestionGroup | null {
  if (!isObject(value)) return null;
  return {
    passage: parsePassage(value.passage),
    questions: (Array.isArray(value.questions) ? value.questions : [])
      .map((q): SittingQuestion | null => {
        const item = parseItem(q);
        return item && isObject(q) ? { ...item, response: parseResponse(q.response) } : null;
      })
      .filter((q): q is SittingQuestion => q !== null),
  };
}

/** « Questions 11–15 »: the first and last numbers of the questions shown (null without any). */
export function questionRange(questions: Pick<Item, "number" | "numbers">[]): { from: number; to: number } | null {
  const all = questions.flatMap((q, i) => (q.numbers.length ? q.numbers : [q.number ?? i + 1]));
  return all.length ? { from: Math.min(...all), to: Math.max(...all) } : null;
}

export function parseTask(value: unknown): IeltsTask | null {
  if (!isObject(value)) return null;
  const limits = isObject(value.limits) ? value.limits : {};
  const examiner = isObject(value.examiner) ? value.examiner : {};
  const card = isObject(examiner.cue_card) ? examiner.cue_card : null;
  return {
    id: str(value.id),
    number: num(value.number),
    prompt: str(value.prompt),
    bullets: (Array.isArray(value.documents) ? value.documents : [])
      .map((d) => (isObject(d) ? str(d.text) || str(d.title) : str(d)))
      .filter(Boolean),
    minWords: numOrNull(limits.min_words),
    suggestedMinutes: numOrNull(limits.suggested_minutes),
    prepSeconds: num(limits.prep_seconds),
    seconds: num(limits.seconds),
    label: localizedText(limits.label),
    letterType: str(value.letter_type),
    text: str(value.text),
    status: str(value.status),
    hasAudio: value.has_audio === true,
    cueCard: card ? { topic: str(card.topic), bullets: strings(card.bullets) } : null,
    prepUntil: str(examiner.prep_until) || null,
    topics: strings(examiner.topics),
  };
}

export function parseIeltsState(value: unknown): IeltsState {
  const data = isObject(value) ? value : {};
  const section = data.section === "pl" ? "pl" : isIeltsSkill(data.section) ? data.section : "";
  return {
    status: str(data.status),
    exam: str(data.exam),
    kind: str(data.kind),
    mode: str(data.mode),
    section,
    remaining: num(data.remaining_seconds),
    skills: strings(data.skills),
    answered: num(data.answered),
    total: num(data.total),
    part: num(data.part, 1),
    parts: num(data.parts),
    navigable: data.navigable === true,
    reviewMinutes: num(data.review_minutes),
    current: parseQuestionGroup(data.current),
    sections: (Array.isArray(data.sections) ? data.sections : [])
      .map(parseQuestionGroup)
      .filter((g): g is QuestionGroup => g !== null),
    tasks: (Array.isArray(data.tasks) ? data.tasks : []).map(parseTask).filter((t): t is IeltsTask => t !== null),
    question: parseItem(data.question),
    level: str(data.level),
    results: parseIeltsResults(data.results),
  };
}

// --- Results -------------------------------------------------------------------------------------

export interface BandResult {
  band: number | null;
  clb: number | null;
  cefr: string;
  raw: number | null;
  outOf: number | null;
  raw40: number | null;
  pending: boolean;
  tasks: Record<string, number | null>;
  /** D-171: planned tasks left empty (they count 0), out of the planned tasks. */
  unanswered: number;
  tasksPlanned: number;
}

export interface PlacementResult {
  level: string;
  band: number | null;
  cefr: string;
  beginner: boolean;
  answered: number;
  right: number;
}

export interface IeltsResults {
  skills: Partial<Record<IeltsSkill, BandResult>>;
  overall: number | null;
  placement: PlacementResult | null;
}

/** ielts.skill_result: `band` (and the same value in `score`), `clb` (and `nclc`), CEFR, raw marks. */
export function parseIeltsResults(value: unknown): IeltsResults {
  const data = isObject(value) ? value : {};
  const skills: Partial<Record<IeltsSkill, BandResult>> = {};
  for (const skill of IELTS_SKILLS) {
    const row = data[skill];
    if (!isObject(row)) continue;
    const tasks: Record<string, number | null> = {};
    if (isObject(row.tasks)) for (const [k, v] of Object.entries(row.tasks)) tasks[k] = numOrNull(v);
    skills[skill] = {
      band: numOrNull(row.band) ?? numOrNull(row.score),
      clb: numOrNull(row.clb) ?? numOrNull(row.nclc),
      cefr: str(row.cefr),
      raw: numOrNull(row.raw),
      outOf: numOrNull(row.out_of),
      raw40: numOrNull(row.raw_40),
      pending: row.pending === true,
      tasks,
      unanswered: num(row.unanswered),
      tasksPlanned: num(row.tasks_planned),
    };
  }
  const overall = isObject(data.overall) ? numOrNull(data.overall.band) : null;
  const placement = isObject(data.placement)
    ? {
        level: str(data.placement.level),
        band: numOrNull(data.placement.band),
        cefr: str(data.placement.cefr),
        beginner: data.placement.beginner === true,
        answered: num(data.placement.answered),
        right: num(data.placement.right),
      }
    : null;
  return { skills, overall, placement };
}

/** An IELTS review or results: the questions carry their form (`type`) or the results a band. */
export function isIeltsReview(questions: unknown, results: unknown): boolean {
  const rows = Array.isArray(questions) ? questions : [];
  const data = isObject(results) ? results : {};
  return rows.some((q) => isObject(q) && typeof q.type === "string") || Object.values(data).some((r) => isObject(r) && "band" in r);
}

export interface IeltsReviewQuestion extends Item {
  skill: string;
  part: number | null;
  response: ItemResponse;
  accepted: unknown;
  points: number;
  correct: boolean;
  explanation: string;
}

export interface IeltsCriterion {
  key: string;
  band: number | null;
  comment: string;
}

export interface IeltsGrade {
  band: number | null;
  criteria: IeltsCriterion[];
  errors: { excerpt: string; correction: string; explanation: string }[];
  corrected: string;
  model: string;
  advice: string[];
  comment: string;
}

export interface IeltsProduction {
  skill: string;
  task: number;
  prompt: string;
  text: string;
  wordCount: number;
  status: string;
  grade: IeltsGrade;
  /** D-171: nothing written or said: « non traitée », 0. */
  unanswered: boolean;
  id: string;
  rawGrade: unknown;
  audioUrl: string | null;
  /** D-174: the recording is no longer kept; the transcript and the correction stay. */
  audioExpired: boolean;
  turns: { role: string; text: string }[];
}

export interface IeltsReview {
  results: IeltsResults;
  questions: IeltsReviewQuestion[];
  productions: IeltsProduction[];
}

function parseGrade(value: unknown): IeltsGrade {
  const g = isObject(value) ? value : {};
  return {
    band: numOrNull(g.band),
    criteria: objects(g.criteria).map((c) => ({
      key: str(c.key),
      band: numOrNull(c.band) ?? numOrNull(c.points),
      comment: str(c.comment),
    })),
    errors: objects(g.errors).map((e) => ({
      excerpt: str(e.excerpt),
      correction: str(e.correction),
      explanation: str(e.explanation),
    })),
    corrected: str(g.corrected_version),
    model: str(g.model_answer),
    advice: strings(g.advice).filter(Boolean),
    comment: str(g.comment),
  };
}

/**
 * The review of a sitting (api.py `review`). A production's band is `grade.band`: for the IELTS
 * the server leaves `score` empty (ielts_grading: never a mark out of 20).
 */
export function parseIeltsReview(value: unknown): IeltsReview {
  const data = isObject(value) ? value : {};
  return {
    results: parseIeltsResults(data.results),
    questions: (Array.isArray(data.questions) ? data.questions : [])
      .map((q, index): IeltsReviewQuestion | null => {
        const item = parseItem(q);
        if (!item || !isObject(q)) return null;
        return {
          ...item,
          id: item.id || `r${index}`,
          skill: str(q.skill),
          part: numOrNull(q.part),
          response: parseResponse(q.response),
          accepted: q.accepted ?? q.answer ?? null,
          points: num(q.points),
          correct: q.correct === true,
          explanation: str(q.explanation),
        };
      })
      .filter((q): q is IeltsReviewQuestion => q !== null),
    productions: objects(data.productions).map((p) => ({
      skill: str(p.skill),
      task: num(p.task),
      prompt: str(p.prompt),
      text: str(p.text),
      wordCount: num(p.word_count),
      status: str(p.status),
      grade: parseGrade(p.grade),
      unanswered: isObject(p.grade) && p.grade.unanswered === true,
      id: str(p.id),
      rawGrade: p.grade,
      audioUrl: str(p.audio_url) || null,
      audioExpired: p.audio_expired === true,
      turns: objects(p.turns)
        .map((turn) => ({ role: str(turn.role), text: str(turn.text) }))
        .filter((turn) => turn.text),
    })),
  };
}
